import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { fetchCategories } from '@/lib/queries/categories';
import type { CategoryRow } from '@/lib/queries/categories';

export type Category = CategoryRow;

export const useCategories = (tenantId: string | null) => {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const { data: categories = [], isLoading, refetch } = useQuery({
    queryKey: ['categories', tenantId],
    queryFn: async () => {
      if (!tenantId || !user) return [] as Category[];
      return fetchCategories(tenantId);
    },
    enabled: !!tenantId && !!user,
    staleTime: 30 * 60 * 1000, // 30 minutes
  });

  const createCategory = async (category: Omit<Category, 'id' | 'created_at' | 'updated_at'>) => {
    if (!user || !tenantId) return { data: null, error: 'Not authenticated or no tenant selected' };

    try {
      const { data, error } = await supabase
        .from('appointment_type_categories')
        .insert([{ ...category, tenant_id: tenantId }])
        .select()
        .single();

      if (error) throw error;

      // Invalidate and refetch categories
      queryClient.invalidateQueries({ queryKey: ['categories', tenantId] });

      return { data, error: null as any };
    } catch (error) {
      console.error('Error creating category:', error);
      return { data: null, error: error as any };
    }
  };

  const updateCategory = async (id: string, updates: Partial<Category>) => {
    try {
      const { data, error } = await supabase
        .from('appointment_type_categories')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;

      // Invalidate and refetch categories
      queryClient.invalidateQueries({ queryKey: ['categories', tenantId] });

      return { data, error: null as any };
    } catch (error) {
      console.error('Error updating category:', error);
      return { data: null, error: error as any };
    }
  };

  const deleteCategory = async (id: string) => {
    try {
      const { error } = await supabase
        .from('appointment_type_categories')
        .delete()
        .eq('id', id);

      if (error) throw error;

      // Invalidate and refetch categories
      queryClient.invalidateQueries({ queryKey: ['categories', tenantId] });

      return { error: null };
    } catch (error) {
      console.error('Error deleting category:', error);
      return { error };
    }
  };

  const findOrCreateCategory = async (name: string): Promise<{ data: Category | null, error: string | null }> => {
    if (!user || !tenantId) return { data: null, error: 'Not authenticated or no tenant selected' };

    // First check if category already exists
    const existingCategory = categories.find(c => c.name.toLowerCase() === name.toLowerCase());
    if (existingCategory) {
      return { data: existingCategory, error: null };
    }

    // Create new category
    const newCategory = {
      name: name.trim(),
      tenant_id: tenantId,
      display_order: categories.length,
      is_active: true,
      description: null as string | null,
      icon: null as string | null,
    };

    return await createCategory(newCategory);
  };

  return {
    categories,
    isLoading,
    createCategory,
    updateCategory,
    deleteCategory,
    findOrCreateCategory,
    refetch,
  };
};
